import axios from 'axios';
import { useQueryClient } from 'react-query';
import { WorkOrder } from '../models/workOrder';
import { ProposalStatus } from '../models/proposal';
import { ProjectLeadAssignment } from '../models/project';

// The extra calls behind the proposal approval wizard (item 49). Approving the
// proposal itself goes through useProposal().tryChangeStatus (POST
// proposal/SetStatus) — the backend auto-creates the work order. These two
// round out the wizard: read back that work order, then optionally generate the
// deposit invoice from it (which flips the proposal to Approved Deposited
// server-side).

const apiUrl = () => process.env.REACT_APP_BASIX_API || '';

export interface GenerateInvoiceInput {
  workOrderId: string;
  invoiceNumber: string;
  itemize: boolean;
  // item 433 — optional deposit-split override (proposal-driven deposit invoices only)
  depositPercentOverride?: number | null;
}

const useProposalApproval = () => {
  const queryClient = useQueryClient();

  // approve the proposal (backend auto-creates the work order). Async so the
  // wizard can wait for it before reading the work order back. item 30/49/164: the
  // approval wizard supplies the project leads + % split (POST proposal/Approve/{id})
  // — project-lead-payout items accrue, split across the leads by their %.
  const approveProposal = async (
    proposalId: string,
    projectLeads?: ProjectLeadAssignment[],
    rnwySaleType?: number, // item 240 — RNWY sale type chosen at approval (0 = N/A)
    depositPercent?: number, // item 433 — deposit split chosen at approval (default 50)
    poNumber?: string, // item 611 — customer PO captured in the wizard's first step (optional)
    rnwyHelpDeskTier?: number, // dart (2026-07-15, Chad) — Phase A Help Desk tier (0 = None)
    rnwySwitchPilotDeclined?: boolean, // item 786 — the client declined the quoted SwitchPilot
    rnwyThirdPartyDeclined?: boolean, // item 789 — the client declined the quoted Third-Party
    noLeadCommission?: boolean, // item 1174 — the signed no-lead-commission declaration
  ): Promise<void> => {
    await axios.post(
      // dart (2026-07-14) — sendEmail=false: the wizard PROMPTS to send the thank-you AFTER the
      // deposit invoice is generated (see sendApprovalEmail), instead of auto-sending here.
      `${apiUrl()}proposal/Approve/${proposalId}?sendEmail=false`,
      {
        projectLeads: projectLeads ?? [],
        rnwySaleType: rnwySaleType ?? 0,
        depositPercent: depositPercent ?? null,
        // item 611 — always send the field (even when blank) so the wizard's PO entry is
        // the authoritative capture; the backend maps blank → null and never blocks on it.
        poNumber: poNumber ?? '',
        rnwyHelpDeskTier: rnwyHelpDeskTier ?? 0,
        rnwySwitchPilotDeclined: rnwySwitchPilotDeclined ?? false,
        rnwyThirdPartyDeclined: rnwyThirdPartyDeclined ?? false,
        noLeadCommission: noLeadCommission ?? false, // item 1174
      },
    );
    await Promise.all([
      queryClient.invalidateQueries('proposals'),
      queryClient.invalidateQueries('workOrders'),
      // the project DETAIL query (['project', id]) embeds proposals/workOrders/
      // invoices — refresh it too so the project view shows the new work order
      // without a manual reload
      queryClient.invalidateQueries('project'),
    ]);
  };

  // dart (2026-07-14) — send the approval thank-you (APPROVED PDF attached) on the user's say-so,
  // after the deposit invoice is generated. Blank `to` → backend uses the customer's email on file.
  const sendApprovalEmail = async (proposalId: string, to?: string): Promise<void> => {
    await axios.post(`${apiUrl()}proposal/${proposalId}/send-approval-email`, { to: to ?? '' });
  };

  const getWorkOrderByProposal = async (proposalId: string): Promise<WorkOrder> => {
    const { data } = await axios.get(`${apiUrl()}WorkOrder/ByProposal/${proposalId}`);
    return data;
  };

  // complete the proposal (item 109) — used by the completion wizard after the
  // final invoice is generated (or on "complete without invoicing")
  const completeProposal = async (proposalId: string): Promise<void> => {
    await axios.post(
      `${apiUrl()}proposal/SetStatus/${proposalId}`,
      ProposalStatus.Complete,
      { headers: { 'content-type': 'application/json' } },
    );
    await Promise.all([
      queryClient.invalidateQueries('proposals'),
      queryClient.invalidateQueries('workOrders'),
      queryClient.invalidateQueries('invoices'),
      queryClient.invalidateQueries('project'),
    ]);
  };

  const generateDepositInvoice = async (input: GenerateInvoiceInput): Promise<string> => {
    const { data } = await axios.post(`${apiUrl()}invoice`, input);
    // the new invoice + flipped statuses ripple across these lists + the project detail
    await Promise.all([
      queryClient.invalidateQueries('invoices'),
      queryClient.invalidateQueries('workOrders'),
      queryClient.invalidateQueries('proposals'),
      queryClient.invalidateQueries('project'),
    ]);
    // backend returns the new invoice GUID — caller can navigate to /invoice/:id
    return data as string;
  };

  return {
    approveProposal, getWorkOrderByProposal, generateDepositInvoice, completeProposal, sendApprovalEmail,
  };
};

export default useProposalApproval;
